/**
 * GMA-1622 (CompassCare Web 7/14 — Dispatch) — call-tree simulation model.
 *
 * Pure, deterministic simulation of an escalation call tree (L1 primary → L2
 * secondary → L3 neighbor/care team → L4 dispatch/emergency). Given fake
 * per-level responses it produces the timeline the "Test Call Tree" mode shows.
 * It NEVER contacts anyone — the result is flagged `simulated: true`.
 *
 * Fails safe: a malformed tree yields an empty, unresolved (but still
 * `simulated`) result rather than a throw.
 */

export const CONTACT_LEVELS = Object.freeze(["L1", "L2", "L3", "L4"]);

export const CONTACT_METHODS = Object.freeze(["call", "text", "email", "app"]);

// The recognized per-level response outcomes. Anything else (missing/unknown)
// normalizes to "no_answer" so the timeline only ever contains valid outcomes.
export const CALL_OUTCOMES = Object.freeze(["answered", "no_answer", "declined"]);

// A level is "resolved" only when the contact answers. No-answer and declined
// both escalate to the next level.
const RESOLVING_OUTCOME = "answered";

/**
 * Walk a call tree with fake per-level responses and return the resulting
 * timeline + resolution.
 *
 * @param {{levels?: Array<{level?: string, contactName?: (string|null),
 *   methods?: string[], escalationMs?: number, retries?: number}>}|null} [tree]
 *   Null/malformed trees and missing fields are tolerated (fail-safe):
 *   unknown/missing levels are skipped, missing methods → null method,
 *   missing/non-finite retries → 1, missing/non-finite escalationMs → 0.
 * @param {Record<string, string>} responses keyed by level id (e.g.
 *   { L1: "no_answer" }); a missing or unrecognized value normalizes to
 *   "no_answer" (see CALL_OUTCOMES).
 * @returns {{simulated: true, resolved: boolean, resolvedBy: (string|null),
 *   totalMs: number, timeline: Array}}
 */
export function simulateCallTree(tree, responses) {
  const levels = tree && Array.isArray(tree.levels) ? tree.levels : [];
  const outcomes = responses && typeof responses === "object" ? responses : {};

  const timeline = [];
  let t = 0;
  let resolvedBy = null;

  for (const lvl of levels) {
    // skip malformed level entries — a missing/invalid `level` must not produce
    // an `undefined` timeline row or (worse) an undefined `resolvedBy`.
    if (!lvl || typeof lvl !== "object" || typeof lvl.level !== "string") {
      continue;
    }
    // Only the documented escalation levels may enter (or resolve) the
    // timeline (Copilot #303) — unknown level ids are skipped like other
    // malformed entries.
    if (!CONTACT_LEVELS.includes(lvl.level)) continue;
    const raw = outcomes[lvl.level];
    const outcome = CALL_OUTCOMES.includes(raw) ? raw : "no_answer";
    timeline.push({
      level: lvl.level,
      contactName: typeof lvl.contactName === "string" ? lvl.contactName : null,
      // First SUPPORTED method only (Copilot #303) — an unknown method string
      // must not leak into the timeline; outcomes are normalized the same way.
      method: Array.isArray(lvl.methods)
        ? (lvl.methods.find((m) => CONTACT_METHODS.includes(m)) ?? null)
        : null,
      atMs: t,
      outcome,
      // Same name in as out (Copilot #303): `retries` is the TOTAL attempt
      // count for the level (min 1), not retries-after-first.
      retries:
        Number.isFinite(lvl.retries) && lvl.retries > 0 ? lvl.retries : 1,
    });
    if (outcome === RESOLVING_OUTCOME) {
      resolvedBy = lvl.level;
      break;
    }
    // No answer / declined → wait this level's configured window, then escalate.
    t += Number.isFinite(lvl.escalationMs) && lvl.escalationMs > 0 ? lvl.escalationMs : 0;
  }

  // `t` is the accumulated elapsed time: when resolved it equals the resolving
  // attempt's start (we break before adding its window); when unresolved it
  // includes every level's escalation window (incl. the last one).
  return {
    simulated: true,
    resolved: resolvedBy !== null,
    resolvedBy,
    totalMs: t,
    timeline,
  };
}
